import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MomDetailDialog } from "@/pages/MomView";
import { MemberSearchSelect } from "@/components/MemberSearchSelect";
import { DeptPicker, DeptChips } from "@/components/DeptPicker";
import { useConfirm } from "@/context/ConfirmContext";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, CheckSquare, CheckCircle2, Circle, AlertTriangle, Clock, CalendarDays, Plus, Trash2, UserPlus, MessageSquare } from "lucide-react";

const KIND = {
  overdue: { cls: "text-destructive", label: "Overdue" },
  due: { cls: "text-amber-600", label: "Due soon" },
  reminder: { cls: "text-sky-600", label: "Reminder" },
  point: { cls: "text-muted-foreground", label: "Discussion point" },
  none: { cls: "text-muted-foreground", label: "No deadline" },
};

function MomTodoRow({ item, onToggle, onOpen, busy }) {
  const done = item.done;
  const k = KIND[item.kind] || KIND.none;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border p-3 hover:border-primary/40 transition-colors" data-testid={`mytodo-${item.todo_id}`}>
      <button onClick={() => onToggle(item)} disabled={busy} className="mt-0.5 shrink-0" data-testid={`mytodo-toggle-${item.todo_id}`}>
        {done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
      </button>
      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onOpen(item.mom_id)} data-testid={`mytodo-open-${item.todo_id}`}>
        <p className={`text-sm break-words ${done ? "line-through text-muted-foreground" : ""}`}>{item.text}</p>
        <p className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{item.mom_title}</span>
          {item.deadline && <span>📅 {item.deadline}</span>}
          {!done && item.kind !== "none" && <span className={k.cls}>· {k.label}</span>}
          {done && item.done_at && <span className="text-emerald-600">✓ done {item.done_at.slice(0, 10)}</span>}
        </p>
      </div>
    </div>
  );
}

function TaskRow({ t, showAssignee, showCreator, onToggle, onDelete, busy }) {
  const { user } = useAuth();
  const overdue = !t.done && t.deadline && t.deadline < new Date().toISOString().slice(0, 10);
  const canDelete = t.created_by === user.id;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border p-3 hover:border-primary/40 transition-colors" data-testid={`task-${t.id}`}>
      <button onClick={() => onToggle(t)} disabled={busy} className="mt-0.5 shrink-0" data-testid={`task-toggle-${t.id}`}>
        {t.done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-sm break-words ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.text}</p>
        <p className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {showAssignee && <span className="flex items-center gap-1"><UserPlus className="h-3 w-3" />{t.assignee_name}</span>}
          {showCreator && <span>from {t.created_by_name}</span>}
          {t.deadline && <span className={overdue ? "text-destructive font-medium" : ""}>📅 {t.deadline}{overdue ? " · overdue" : ""}</span>}
          <DeptChips list={t.departments} testid={`task-depts-${t.id}`} />
        </p>
        {t.done && t.completion_note && (
          <p className="text-[11px] text-foreground/70 mt-1.5 flex items-start gap-1 rounded bg-secondary/60 px-2 py-1" data-testid={`task-note-${t.id}`}>
            <MessageSquare className="h-3 w-3 mt-0.5 shrink-0 text-muted-foreground" /> <span className="break-words whitespace-pre-wrap">{t.completion_note}</span>
          </p>
        )}
      </div>
      {canDelete ? (
        <button onClick={() => onDelete(t)} disabled={busy} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`task-delete-${t.id}`} title="Delete task"><Trash2 className="h-4 w-4" /></button>
      ) : <span className="w-4 shrink-0" />}
    </div>
  );
}

export default function MyTodosView() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [data, setData] = useState({ open: [], done: [], open_count: 0 });
  const [tasks, setTasks] = useState({ assigned_to_me: [], i_assigned: [] });
  const [dir, setDir] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [active, setActive] = useState(null);
  const [nt, setNt] = useState({ text: "", assignee: "", deadline: "", departments: [] });
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [todos, tk, d] = await Promise.all([api.get("/my-todos"), api.get("/tasks"), api.get("/directory")]);
      setData(todos.data); setTasks(tk.data); setDir(d.data);
    } catch (e) { toast.error("Could not load your to-dos"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    setBusy(item.todo_id);
    try { await api.patch(`/moms/${item.mom_id}/todos/${item.todo_id}`, { done: !item.done }); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  const toggleTask = async (t) => {
    if (!t.done) { setCompleting({ id: t.id, text: t.text, note: "" }); return; }
    setBusy(t.id);
    try { await api.patch(`/tasks/${t.id}`, { done: false }); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  const confirmComplete = async () => {
    if (!completing) return;
    setBusy(completing.id);
    try {
      await api.patch(`/tasks/${completing.id}`, { done: true, note: completing.note });
      setCompleting(null);
      await load();
      toast.success("Task marked done");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  const deleteTask = async (t) => {
    if (!(await confirm({ title: "Delete this task?", description: "This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    setBusy(t.id);
    try { await api.delete(`/tasks/${t.id}`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  const addTask = async () => {
    if (!nt.text.trim()) { toast.error("Enter a task"); return; }
    if (!nt.assignee) { toast.error("Pick a member to assign"); return; }
    setSaving(true);
    try {
      await api.post("/tasks", { text: nt.text.trim(), assignee_id: nt.assignee, deadline: nt.deadline, departments: nt.departments });
      toast.success("Task assigned");
      setNt({ text: "", assignee: "", deadline: "", departments: [] });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const myTasksOpen = tasks.assigned_to_me.filter((t) => !t.done);
  const myTasksDone = tasks.assigned_to_me.filter((t) => t.done);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><CheckSquare className="h-5 w-5" /></div>
        <div>
          <h2 className="font-heading text-xl font-bold">To Do</h2>
          <p className="text-sm text-muted-foreground">Assign tasks to anyone and track every action item from meetings.</p>
        </div>
        {data.open_count + myTasksOpen.length > 0 && (
          <Badge className="ml-auto rounded-full border-0 bg-primary text-primary-foreground" data-testid="mytodos-open-count">{data.open_count + myTasksOpen.length} open</Badge>
        )}
      </div>

      {/* Assign a task */}
      <Card className="p-4 sm:p-5 rounded-xl mb-6" data-testid="assign-task-card">
        <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><Plus className="h-4 w-4" /> Assign a task</h3>
        <div className="grid sm:grid-cols-[1fr_220px_170px_auto] gap-2 items-end">
          <div className="space-y-1"><Label className="text-xs">Task</Label><Input value={nt.text} onChange={(e) => setNt({ ...nt, text: e.target.value })} placeholder="What needs doing?" data-testid="task-text-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Assign to</Label><MemberSearchSelect people={dir} value={nt.assignee} onChange={(v) => setNt({ ...nt, assignee: v })} testid="task-assignee-select" placeholder="Search a member…" clearable /></div>
          <div className="space-y-1"><Label className="text-xs">Deadline (optional)</Label><Input type="date" value={nt.deadline} onChange={(e) => setNt({ ...nt, deadline: e.target.value })} data-testid="task-deadline-input" /></div>
          <Button onClick={addTask} disabled={saving} className="rounded-lg" data-testid="task-add-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign"}</Button>
        </div>
        <div className="mt-3 space-y-1"><Label className="text-xs">Departments (optional)</Label><DeptPicker value={nt.departments} onChange={(v) => setNt({ ...nt, departments: v })} testid="task-dept-picker" /></div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6">
          {/* Tasks assigned to me */}
          <div>
            <h3 className="font-heading font-semibold text-sm mb-2">Assigned to me</h3>
            {myTasksOpen.length === 0 ? (
              <Card className="p-6 text-center rounded-xl" data-testid="tasks-mine-empty"><p className="text-sm text-muted-foreground">No open tasks assigned to you.</p></Card>
            ) : (
              <div className="space-y-2" data-testid="tasks-mine-list">
                {myTasksOpen.map((t) => <TaskRow key={t.id} t={t} showCreator onToggle={toggleTask} onDelete={deleteTask} busy={busy === t.id} />)}
              </div>
            )}
          </div>

          {/* From meetings (MOM) */}
          <div>
            <h3 className="font-heading font-semibold text-sm mb-2">From meetings (MOM)</h3>
            {data.open.length === 0 ? (
              <Card className="p-6 text-center rounded-xl" data-testid="mytodos-open-empty"><p className="text-sm text-muted-foreground">No open action items from meetings 🎉</p></Card>
            ) : (
              <div className="space-y-2" data-testid="mytodos-open-list">
                {data.open.map((it) => <MomTodoRow key={it.todo_id} item={it} onToggle={toggle} onOpen={setActive} busy={busy === it.todo_id} />)}
              </div>
            )}
          </div>

          {/* I assigned to others */}
          {tasks.i_assigned.length > 0 && (
            <div>
              <h3 className="font-heading font-semibold text-sm mb-2">I assigned to others</h3>
              <div className="space-y-2" data-testid="tasks-iassigned-list">
                {tasks.i_assigned.map((t) => <TaskRow key={t.id} t={t} showAssignee onToggle={toggleTask} onDelete={deleteTask} busy={busy === t.id} />)}
              </div>
            </div>
          )}

          {/* Completed */}
          {(myTasksDone.length > 0 || data.done.length > 0) && (
            <div>
              <h3 className="font-heading font-semibold text-sm mb-2 text-muted-foreground">Recently completed</h3>
              <div className="space-y-2" data-testid="mytodos-done-list">
                {myTasksDone.map((t) => <TaskRow key={t.id} t={t} showCreator onToggle={toggleTask} onDelete={deleteTask} busy={busy === t.id} />)}
                {data.done.map((it) => <MomTodoRow key={it.todo_id} item={it} onToggle={toggle} onOpen={setActive} busy={busy === it.todo_id} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {active && <MomDetailDialog momId={active} me={user} onClose={() => { setActive(null); load(); }} />}

      <Dialog open={!!completing} onOpenChange={(o) => !o && setCompleting(null)}>
        <DialogContent data-testid="task-complete-dialog">
          <DialogHeader>
            <DialogTitle>Mark task done</DialogTitle>
            <DialogDescription className="break-words">{completing?.text}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Add a short note about what you did (optional)</label>
            <Textarea rows={3} value={completing?.note || ""} onChange={(e) => setCompleting((p) => ({ ...p, note: e.target.value }))} placeholder="e.g. Shipped the fix and shared the doc with the team" data-testid="task-complete-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setCompleting(null)} data-testid="task-complete-cancel">Cancel</Button>
            <Button className="rounded-lg" onClick={confirmComplete} disabled={busy === completing?.id} data-testid="task-complete-confirm">Mark done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
